import { useEffect, useRef, useState } from 'react';

interface UseAutoRotateOptions {
  /** Pocet polozek v rotaci. */
  total: number;
  /** Interval v ms. Pokud <=0, rotace neprobiha. */
  intervalMs: number;
  /** Pausa po hover / click v ms (default 10 s). */
  pauseAfterInteractMs?: number;
}

/**
 * Hook pro auto-rotaci indexu v poli. Vraci { index, setIndex, pause, resume }.
 * Automaticky se pauzuje na pauseAfterInteractMs po volani setIndex (predpoklad:
 * user chce soucasny stav zachovat po kliknuti).
 */
export const useAutoRotate = ({ total, intervalMs, pauseAfterInteractMs = 10_000 }: UseAutoRotateOptions) => {
  const [index, setIndexInternal] = useState(0);
  const pausedUntilRef = useRef<number>(0);

  useEffect(() => {
    if (total <= 1 || intervalMs <= 0) return;
    const tick = setInterval(() => {
      if (Date.now() < pausedUntilRef.current) return;
      setIndexInternal((i) => (i + 1) % total);
    }, intervalMs);
    return () => clearInterval(tick);
  }, [total, intervalMs]);

  // Bezpecnost — pokud se total zmeni a index je mimo, resetuj
  useEffect(() => {
    if (index >= total && total > 0) setIndexInternal(0);
  }, [index, total]);

  const setIndex = (newIndex: number) => {
    pausedUntilRef.current = Date.now() + pauseAfterInteractMs;
    setIndexInternal(Math.max(0, Math.min(newIndex, Math.max(0, total - 1))));
  };

  return { index, setIndex };
};
